import { useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { GitCompare, Plus, Minus, Pencil } from "lucide-react";
import { lineDiff } from "@/lib/diff";

const bgFor = (t) => ({
  same: "bg-white",
  added: "bg-emerald-50",
  removed: "bg-rose-50",
  changed: "bg-amber-50",
}[t] || "bg-white");
const gutter = (t) => ({ same: "", added: "text-emerald-600", removed: "text-rose-600", changed: "text-amber-600" }[t] || "");
const glyph = (t) => t === "added" ? <Plus className="w-3 h-3" /> : t === "removed" ? <Minus className="w-3 h-3" /> : t === "changed" ? <Pencil className="w-3 h-3" /> : null;

export default function SOPDiffDialog({ open, onClose, oldSop, newSop, onAccept }) {
  const diff = useMemo(() => lineDiff(oldSop?.content || "", newSop?.content || ""), [oldSop, newSop]);
  if (!oldSop || !newSop) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-5xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="sop-diff-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <GitCompare className="w-5 h-5 text-pink-700" /> Compare — {oldSop.title}
          </DialogTitle>
          <div className="flex flex-wrap gap-3 text-xs mt-1">
            <span className="text-emerald-700 font-mono">+{diff.stats.added} added</span>
            <span className="text-rose-700 font-mono">-{diff.stats.removed} removed</span>
            <span className="text-amber-700 font-mono">~{diff.stats.changed} changed</span>
            <span className="text-slate-500 font-mono">{diff.stats.same} unchanged</span>
            {oldSop.version !== newSop.version && (
              <span className="text-pink-700 font-mono">v{oldSop.version} → v{newSop.version}</span>
            )}
          </div>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-1 text-xs flex-1 overflow-hidden">
          <div className="border border-slate-200 rounded-lg overflow-auto">
            <div className="sticky top-0 bg-slate-100 px-3 py-2 font-semibold text-slate-700 border-b border-slate-200">Current (on file)</div>
            {diff.rows.map((r) => (
              <div key={`old-${r.id}`} className={`flex items-start gap-2 px-2 py-0.5 ${bgFor(r.type === "added" ? "same" : r.type)}`}>
                <span className={`w-3 shrink-0 ${gutter(r.type === "added" ? "same" : r.type)}`}>{r.type !== "same" && r.type !== "added" ? glyph(r.type) : ""}</span>
                <pre className="whitespace-pre-wrap font-mono text-[11px] leading-snug flex-1">{r.old || " "}</pre>
              </div>
            ))}
          </div>
          <div className="border border-slate-200 rounded-lg overflow-auto">
            <div className="sticky top-0 bg-pink-50 px-3 py-2 font-semibold text-pink-900 border-b border-pink-200">AI-reformatted upload</div>
            {diff.rows.map((r) => (
              <div key={`new-${r.id}`} className={`flex items-start gap-2 px-2 py-0.5 ${bgFor(r.type === "removed" ? "same" : r.type)}`}>
                <span className={`w-3 shrink-0 ${gutter(r.type === "removed" ? "same" : r.type)}`}>{r.type !== "same" && r.type !== "removed" ? glyph(r.type) : ""}</span>
                <pre className="whitespace-pre-wrap font-mono text-[11px] leading-snug flex-1">{r.new || " "}</pre>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="diff-accept" onClick={() => onAccept(newSop)} className="bg-pink-700 hover:bg-pink-800">Replace with new version</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
